import {
  FIXTURE_ALERTS,
  FIXTURE_ALERT_QUALITY,
  FIXTURE_COVERAGE,
  FIXTURE_AGENTS,
  FIXTURE_INCIDENTS,
  FIXTURE_KB_BY_SERVICE,
  FIXTURE_NOTIFICATIONS,
  FIXTURE_PEOPLE,
  FIXTURE_SEARCH_INDEX,
  FIXTURE_SERVICES,
  FIXTURE_SILENCES,
  FIXTURE_SOPS,
  FIXTURE_SOP_BY_SLUG,
  FIXTURE_SOP_COVERAGE,
  FIXTURE_WHOAMI,
  buildFixtureIncidentDetail,
  buildFixtureServiceDetail,
} from './fixtures'
import type {
  AccessReviewCampaignDetailEnvelope,
  AccessReviewCampaignListEnvelope,
  AccountCreditListEnvelope,
  Alert,
  AlertFilter,
  AutopilotExecutionsEnvelope,
  AutopilotOverview,
  AlertQualityRow,
  BreakGlassListEnvelope,
  CoverageRow,
  CustomersUsageEnvelope,
  CustomerUsageRangeEnvelope,
  Execution,
  FleetEnvelope,
  HomeDashboardData,
  Incident,
  IncidentDetail,
  IncidentFilter,
  InfrastructureCellDetailEnvelope,
  InfrastructureCellListEnvelope,
  InfrastructureNodeDetailEnvelope,
  KBServiceDetail,
  Kpi,
  LeadershipSummary,
  LeadershipSummaryEnvelope,
  Meta,
  MetricsCatalogEnvelope,
  MetricsQueryRangeEnvelope,
  NotificationListEnvelope,
  OrgAgent,
  OrgPerson,
  OrgRolesEnvelope,
  OrgTeamsEnvelope,
  PricebookListEnvelope,
  PricingConfigEnvelope,
  RackBookListEnvelope,
  RotationListEnvelope,
  SearchResult,
  SecurityCategory,
  ServiceCustomersEnvelope,
  ServiceDetail,
  ServiceSignalsEnvelope,
  ServiceSignalsRangeEnvelope,
  ServiceVerbMetricsEnvelope,
  ServiceLatencyEnvelope,
  ServiceSLOBurnEnvelope,
  ServiceSummary,
  Silence,
  Sop,
  SopAnalyticsEnvelope,
  SopCoverageRow,
  SopDetail,
  SopGraphDetailEnvelope,
  SopGraphListEnvelope,
  SopGraphVersionEnvelope,
  SopRunDetailEnvelope,
  SopRunListEnvelope,
  Source,
  StudioAgentConfig,
  TenantDetailEnvelope,
  TenantListEnvelope,
  TerminalFilter,
  TerminalRecordingEnvelope,
  TerminalReviewListEnvelope,
  TerminalSessionEnvelope,
  TerminalSessionListEnvelope,
  TimelineEntry,
  VulnListEnvelope,
  WhoAmIEnvelope,
} from './types'
import { type PresetWindow, type WindowSel, windowParams } from '@/routes/service/signalsWindow'

/** Serialize a range selection to an encoded query string (`window=<preset>` or
 * `window=custom&start=&end=`), the single wire mapping shared by every range
 * endpoint's fetch — presets AND deep-linkable custom spans. */
function windowQuery(sel: WindowSel): string {
  return new URLSearchParams(windowParams(sel)).toString()
}

/**
 * `/admin/v2` is live (r0-api, ADR-0002 D6). VITE_USE_FIXTURES=true is an
 * offline-dev escape hatch only — the default build always hits the real
 * network and never presents sample data as live (FixturesBanner surfaces
 * whenever this is on).
 */
export const USE_FIXTURES = import.meta.env.VITE_USE_FIXTURES === 'true'

const FIXTURE_SOURCE: Source = { kind: 'fixtures-sample-data', observed_at: '2026-07-06T12:00:00Z', stale: false }

/** Thrown by request() on a non-2xx response; status drives the 401-shows-login behavior. */
export class AdminApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // credentials: 'include' is required on every call: the session lives in
  // an HttpOnly cookie (r0-authn) — omitting it silently degrades every
  // request back to "unauthenticated" with no visible error.
  const res = await fetch(`/admin/v2${path}`, { ...init, credentials: 'include' })
  if (!res.ok) {
    throw new AdminApiError(res.status, `admin/v2 ${path} failed: ${res.status} ${res.statusText}`)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

function postJSON<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
}

/**
 * Email + password sign-in. This REPLACED the four WebAuthn ceremony calls
 * (registerBegin/Finish, loginBegin/Finish), whose endpoints no longer exist
 * server-side.
 *
 * The passkey lane was phishing-resistant and hardware-bound in a way a password
 * is not, so this is a real downgrade and it was made deliberately: the lane had
 * become unusable. A credential registered from a phone (transports
 * "hybrid,internal") had its cross-device assertion rejected on every attempt,
 * and it failed undiagnosably — the server discarded the underlying go-webauthn
 * error and answered a flat "Invalid email, code, or passkey", so nothing in the
 * logs or the response said which of the credential, the ceremony or the
 * signature was at fault.
 *
 * The server now logs the specific reason and still answers generically here. The
 * generic answer is deliberate: telling the caller whether the EMAIL or the
 * PASSWORD was wrong hands an attacker half the credential and confirms which
 * admin addresses exist. Do not "improve" the error message.
 */
export async function login(email: string, password: string): Promise<WhoAmIEnvelope> {
  return postJSON<WhoAmIEnvelope>('/auth/login', { email, password })
}

export async function logout(): Promise<void> {
  await request('/auth/logout', { method: 'POST' })
}

export async function fetchWhoAmI(): Promise<WhoAmIEnvelope> {
  if (USE_FIXTURES) return FIXTURE_WHOAMI
  return request<WhoAmIEnvelope>('/auth/whoami')
}

export async function fetchMeta(): Promise<Meta> {
  return request<Meta>('/meta')
}

export async function fetchServices(): Promise<{ services: ServiceSummary[]; source: Source }> {
  if (USE_FIXTURES) return { services: FIXTURE_SERVICES, source: FIXTURE_SOURCE }
  return request<{ services: ServiceSummary[]; source: Source }>('/services')
}

export async function fetchService(key: string): Promise<{ service: ServiceDetail; source: Source }> {
  if (USE_FIXTURES) return { service: buildFixtureServiceDetail(key), source: FIXTURE_SOURCE }
  return request<{ service: ServiceDetail; source: Source }>(`/services/${encodeURIComponent(key)}`)
}

export async function fetchCoverage(): Promise<{ coverage: CoverageRow[]; source: Source }> {
  if (USE_FIXTURES) return { coverage: FIXTURE_COVERAGE, source: FIXTURE_SOURCE }
  return request<{ coverage: CoverageRow[]; source: Source }>('/coverage')
}

export async function fetchPeople(): Promise<{ people: OrgPerson[]; source: Source }> {
  if (USE_FIXTURES) return { people: FIXTURE_PEOPLE, source: FIXTURE_SOURCE }
  return request<{ people: OrgPerson[]; source: Source }>('/org/people')
}

export async function fetchAgents(): Promise<{ agents: OrgAgent[]; source: Source }> {
  if (USE_FIXTURES) return { agents: FIXTURE_AGENTS, source: FIXTURE_SOURCE }
  return request<{ agents: OrgAgent[]; source: Source }>('/org/agents')
}

/** Org management reads (real-integration-1) — the People/Teams/Roles manage surface. No fixtures: the real postgres-store rows, or an honest empty list. */
export async function fetchRoles(): Promise<OrgRolesEnvelope> {
  return request<OrgRolesEnvelope>('/org/roles')
}

export async function fetchTeamsList(): Promise<OrgTeamsEnvelope> {
  return request<OrgTeamsEnvelope>('/org/teams')
}

export async function fetchSearch(q: string): Promise<{ results: SearchResult[]; source: Source }> {
  if (!q.trim()) return { results: [], source: FIXTURE_SOURCE }
  if (USE_FIXTURES) {
    const ql = q.toLowerCase()
    const results = FIXTURE_SEARCH_INDEX.filter((r) => r.name.toLowerCase().includes(ql) || r.key.toLowerCase().includes(ql))
    return { results, source: FIXTURE_SOURCE }
  }
  return request<{ results: SearchResult[]; source: Source }>(`/search?q=${encodeURIComponent(q)}`)
}

export async function fetchKBForService(key: string): Promise<{ kb: KBServiceDetail; source: Source }> {
  if (USE_FIXTURES) {
    return { kb: FIXTURE_KB_BY_SERVICE[key] ?? { service: key, articles: [], sops: [] }, source: FIXTURE_SOURCE }
  }
  return request<{ kb: KBServiceDetail; source: Source }>(`/kb/services/${encodeURIComponent(key)}`)
}

export async function fetchSops(service?: string): Promise<{ sops: Sop[]; source: Source }> {
  if (USE_FIXTURES) {
    const sops = service ? FIXTURE_SOPS.filter((s) => s.services.includes(service)) : FIXTURE_SOPS
    return { sops, source: FIXTURE_SOURCE }
  }
  const q = service ? `?service=${encodeURIComponent(service)}` : ''
  return request<{ sops: Sop[]; source: Source }>(`/sops${q}`)
}

export async function fetchSop(slug: string): Promise<{ sop: SopDetail; source: Source }> {
  if (USE_FIXTURES) {
    const sop = FIXTURE_SOP_BY_SLUG[slug]
    if (!sop) throw new AdminApiError(404, `sop ${slug} not found`)
    return { sop, source: FIXTURE_SOURCE }
  }
  return request<{ sop: SopDetail; source: Source }>(`/sops/${encodeURIComponent(slug)}`)
}

export async function fetchSopCoverage(): Promise<{ coverage: SopCoverageRow[]; source: Source }> {
  if (USE_FIXTURES) return { coverage: FIXTURE_SOP_COVERAGE, source: FIXTURE_SOURCE }
  return request<{ coverage: SopCoverageRow[]; source: Source }>('/sops/coverage')
}

/**
 * SOP graph engine reads (AWU sop-graph-engine) — the versioned flowchart-SOP
 * fabric, distinct from the linear /sops endpoints above. No fixtures by
 * design: real postgres-store rows or an honest empty list. Every graph-SOP
 * MUTATION goes through invokeAction (sop.version.* / sop.graph.* / sop.run.*),
 * never a second write path.
 */
export async function fetchSopGraphs(includeArchived = false): Promise<SopGraphListEnvelope> {
  const q = includeArchived ? '?includeArchived=true' : ''
  return request<SopGraphListEnvelope>(`/sops/graphs${q}`)
}

export async function fetchSopGraph(key: string): Promise<SopGraphDetailEnvelope> {
  return request<SopGraphDetailEnvelope>(`/sops/graphs/${encodeURIComponent(key)}`)
}

export async function fetchSopGraphVersion(key: string, version: number): Promise<SopGraphVersionEnvelope> {
  return request<SopGraphVersionEnvelope>(`/sops/graphs/${encodeURIComponent(key)}/versions/${version}`)
}

export async function fetchSopGraphAnalytics(key: string): Promise<SopAnalyticsEnvelope> {
  return request<SopAnalyticsEnvelope>(`/sops/graphs/${encodeURIComponent(key)}/analytics`)
}

export async function fetchSopRuns(sopKey?: string): Promise<SopRunListEnvelope> {
  const q = sopKey ? `?sop=${encodeURIComponent(sopKey)}` : ''
  return request<SopRunListEnvelope>(`/sops/runs${q}`)
}

export async function fetchSopRun(id: string): Promise<SopRunDetailEnvelope> {
  return request<SopRunDetailEnvelope>(`/sops/runs/${encodeURIComponent(id)}`)
}

function alertQuery(filter?: AlertFilter): string {
  if (!filter) return ''
  const params = new URLSearchParams()
  if (filter.service) params.set('service', filter.service)
  if (filter.team) params.set('team', filter.team)
  if (filter.severity) params.set('severity', filter.severity)
  if (filter.status) params.set('status', filter.status)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

function matchesFilter(a: Alert, filter?: AlertFilter): boolean {
  if (!filter) return true
  if (filter.service && a.service !== filter.service) return false
  if (filter.team && a.owningTeam !== filter.team) return false
  if (filter.severity && a.severity !== filter.severity) return false
  if (filter.status && a.status !== filter.status) return false
  return true
}

export async function fetchAlerts(filter?: AlertFilter): Promise<{ alerts: Alert[]; source: Source }> {
  if (USE_FIXTURES) return { alerts: FIXTURE_ALERTS.filter((a) => matchesFilter(a, filter)), source: FIXTURE_SOURCE }
  return request<{ alerts: Alert[]; source: Source }>(`/alerts${alertQuery(filter)}`)
}

export async function fetchAlert(id: string): Promise<{ alert: Alert; source: Source }> {
  if (USE_FIXTURES) {
    const alert = FIXTURE_ALERTS.find((a) => a.id === id)
    if (!alert) throw new AdminApiError(404, `alert ${id} not found`)
    return { alert, source: FIXTURE_SOURCE }
  }
  return request<{ alert: Alert; source: Source }>(`/alerts/${encodeURIComponent(id)}`)
}

export async function fetchAlertQuality(): Promise<{ quality: AlertQualityRow[]; source: Source }> {
  if (USE_FIXTURES) return { quality: FIXTURE_ALERT_QUALITY, source: FIXTURE_SOURCE }
  return request<{ quality: AlertQualityRow[]; source: Source }>('/alerts/quality')
}

export async function fetchSilences(): Promise<{ silences: Silence[]; source: Source }> {
  if (USE_FIXTURES) return { silences: FIXTURE_SILENCES, source: FIXTURE_SOURCE }
  return request<{ silences: Silence[]; source: Source }>('/silences')
}

/**
 * The current principal's in-portal notification center (the bell). No fixtures
 * on the real path — a person's own persisted notifications, or an honest empty
 * inbox (the dev-token scaffold has none). VITE_USE_FIXTURES returns the
 * offline-dev sample, clearly flagged by the FixturesBanner.
 */
export async function fetchNotifications(): Promise<NotificationListEnvelope> {
  if (USE_FIXTURES) {
    const unreadCount = FIXTURE_NOTIFICATIONS.filter((n) => !n.read && n.lane === 'page').length
    return { notifications: FIXTURE_NOTIFICATIONS, unreadCount, source: FIXTURE_SOURCE }
  }
  return request<NotificationListEnvelope>('/notifications')
}

function incidentQuery(filter?: IncidentFilter): string {
  if (!filter) return ''
  const params = new URLSearchParams()
  if (filter.service) params.set('service', filter.service)
  if (filter.severity) params.set('severity', filter.severity)
  if (filter.status) params.set('status', filter.status)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

function matchesIncidentFilter(i: Incident, filter?: IncidentFilter): boolean {
  if (!filter) return true
  if (filter.service && !i.affectedServices.includes(filter.service)) return false
  if (filter.severity && i.severity !== filter.severity) return false
  if (filter.status && i.status !== filter.status) return false
  return true
}

export async function fetchIncidents(filter?: IncidentFilter): Promise<{ incidents: Incident[]; source: Source }> {
  if (USE_FIXTURES) return { incidents: FIXTURE_INCIDENTS.filter((i) => matchesIncidentFilter(i, filter)), source: FIXTURE_SOURCE }
  return request<{ incidents: Incident[]; source: Source }>(`/incidents${incidentQuery(filter)}`)
}

export async function fetchIncident(id: string): Promise<{ incident: IncidentDetail; source: Source }> {
  if (USE_FIXTURES) return { incident: buildFixtureIncidentDetail(id), source: FIXTURE_SOURCE }
  return request<{ incident: IncidentDetail; source: Source }>(`/incidents/${encodeURIComponent(id)}`)
}

export async function fetchIncidentTimeline(id: string): Promise<{ timeline: TimelineEntry[]; source: Source }> {
  if (USE_FIXTURES) return { timeline: buildFixtureIncidentDetail(id).timeline, source: FIXTURE_SOURCE }
  return request<{ timeline: TimelineEntry[]; source: Source }>(`/incidents/${encodeURIComponent(id)}/timeline`)
}

function terminalQuery(filter?: TerminalFilter): string {
  if (!filter) return ''
  const params = new URLSearchParams()
  if (filter.target) params.set('target', filter.target)
  if (filter.status) params.set('status', filter.status)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/** No fixtures here by design: a terminal session is live security state, never presented as sample data. */
export async function fetchTerminals(filter?: TerminalFilter): Promise<TerminalSessionListEnvelope> {
  return request<TerminalSessionListEnvelope>(`/terminals${terminalQuery(filter)}`)
}

export async function fetchTerminal(id: string): Promise<TerminalSessionEnvelope> {
  return request<TerminalSessionEnvelope>(`/terminals/${encodeURIComponent(id)}`)
}

export async function fetchTerminalRecording(id: string): Promise<TerminalRecordingEnvelope> {
  return request<TerminalRecordingEnvelope>(`/terminals/${encodeURIComponent(id)}/recording`)
}

/** The live session stream's WebSocket URL — same-origin, ws/wss matching the page's own protocol. */
export function terminalStreamURL(id: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}/admin/v2/terminals/${encodeURIComponent(id)}/stream`
}

/**
 * Customer Insight / Support Workspace (M5/M6, ADR-0005 R2). No fixtures
 * here by design — real tenant data sourced from mcd's admin-plane tenant
 * surface, never presented as sample data (same posture as terminals/
 * security).
 */
export async function fetchTenants(): Promise<TenantListEnvelope> {
  return request<TenantListEnvelope>('/tenants')
}

export async function fetchTenant(id: string): Promise<TenantDetailEnvelope> {
  return request<TenantDetailEnvelope>(`/tenants/${encodeURIComponent(id)}`)
}

export async function fetchServiceCustomers(key: string): Promise<ServiceCustomersEnvelope> {
  return request<ServiceCustomersEnvelope>(`/services/${encodeURIComponent(key)}/customers`)
}

/**
 * Per-service RED golden signals (FR-OBS-01/02). No fixtures by design — real
 * signals derived from mcd's prom /metrics via internal/sources, or an honest
 * gap (source.kind:"none") when MC_METRICS_URL is unset / the service has no
 * distinct signal. Never sample data presented as live.
 */
export async function fetchServiceSignals(key: string): Promise<ServiceSignalsEnvelope> {
  return request<ServiceSignalsEnvelope>(`/services/${encodeURIComponent(key)}/signals`)
}

/**
 * The per-service time-series RED signals over a window (the range-picker
 * graphs), sourced from a real Prometheus via /api/v1/query_range through
 * admind. Returns an honest gap (range.gap=true, source.kind "none"/"prom-range")
 * when MC_PROM_URL is unset or the store is unreachable — never fabricated
 * history. `window` is one of the API-supported values (1h/24h/7d).
 */
export async function fetchServiceSignalsRange(
  key: string,
  sel: WindowSel,
): Promise<ServiceSignalsRangeEnvelope> {
  return request<ServiceSignalsRangeEnvelope>(
    `/services/${encodeURIComponent(key)}/signals/range?${windowQuery(sel)}`,
  )
}

/**
 * The per-service per-operation (verb) RED breakdown table, sourced from real
 * Prometheus instant queries (/api/v1/query) joined by handler label through
 * admind. Returns an honest gap (gap=true, source.kind "none"/"prom-range")
 * when MC_PROM_URL is unset or the store is unreachable — never fabricated rows.
 */
export async function fetchServiceVerbMetrics(key: string): Promise<ServiceVerbMetricsEnvelope> {
  return request<ServiceVerbMetricsEnvelope>(`/services/${encodeURIComponent(key)}/metrics/verbs`)
}

/**
 * The per-service latency percentiles (p50/p95/p99) over a window, computed with
 * histogram_quantile over the native latency buckets (ADR-0455) through admind.
 * Honest gap (latency.gap=true) when MC_PROM_URL is unset or the store is
 * unreachable — never a fabricated curve.
 */
export async function fetchServiceLatency(key: string, sel: WindowSel): Promise<ServiceLatencyEnvelope> {
  return request<ServiceLatencyEnvelope>(
    `/services/${encodeURIComponent(key)}/latency?${windowQuery(sel)}`,
  )
}

/**
 * The per-service SLO-burn tiles: each SLO declared in the service's own
 * slos.yaml evaluated against the live store. A tile is either a live
 * value+burn or an honest notEvaluableReason — never a fabricated green.
 * gap=true means no SLO catalog for this service, or no store configured.
 */
export async function fetchServiceSLOBurn(key: string): Promise<ServiceSLOBurnEnvelope> {
  return request<ServiceSLOBurnEnvelope>(`/services/${encodeURIComponent(key)}/slo-burn`)
}

/**
 * The Explore-dock read-only custom PromQL range query (Metrics portal, Founder
 * "full store access"). Generic, not per-service. Guardrails live in admind
 * (length cap → 400, window whitelist, 8MB cap); a bad query or unreachable
 * store returns result.gap=true with Prometheus's verbatim error in source.note
 * — never a fabricated series. `window` is one of 1h/6h/24h/7d.
 */
export async function fetchMetricsQueryRange(
  query: string,
  sel: WindowSel,
): Promise<MetricsQueryRangeEnvelope> {
  return request<MetricsQueryRangeEnvelope>(
    `/metrics/query_range?query=${encodeURIComponent(query)}&${windowQuery(sel)}`,
  )
}

/**
 * The Explore-dock browsable metric catalog — every recorded metric name via
 * Prometheus /api/v1/label/__name__/values through admind. names is empty with
 * source.kind "none" when MC_PROM_URL is unset — an honest gap, never fabricated.
 */
export async function fetchMetricsCatalog(): Promise<MetricsCatalogEnvelope> {
  return request<MetricsCatalogEnvelope>('/metrics/catalog')
}

/**
 * The fleet wall (Founder directive) — every service as a live tile so the sick
 * one jumps out, from THREE grouped by-svc Prometheus queries. Honest gap
 * (fleet.gap=true, tiles present with health only) when MC_PROM_URL is unset or
 * the store is unreachable — never a fabricated flatline. The window is a preset
 * (default 15m, the glance window); custom spans aren't accepted here.
 */
export async function fetchFleet(window: PresetWindow): Promise<FleetEnvelope> {
  return request<FleetEnvelope>(`/metrics/fleet?window=${encodeURIComponent(window)}`)
}

/**
 * The Metrics-portal "Customers & usage" section (brief A3) — per-customer
 * billable-usage table sourced from the mcd operator API's metering pipeline.
 * No fixtures by design: real metering data or an honest gap (gap=true,
 * source.kind "none"/"mc-api") when MC_KMS_API_URL/TOKEN are unset or the
 * metering API is unreachable — never a fabricated customer list.
 */
export async function fetchCustomersUsage(): Promise<CustomersUsageEnvelope> {
  return request<CustomersUsageEnvelope>('/customers/usage')
}

/**
 * The per-customer usage-PATTERN time series (brief A3) — bucketed billable
 * ops over the window (total + per-service split), driven by the range picker.
 * The project is validated server-side against the live projects list (404
 * otherwise). Honest gap (range.gap=true) when the metering API is
 * unconfigured/unreachable — never a fabricated pattern. `window` is one of
 * 1h/6h/24h/7d.
 */
export async function fetchCustomerUsageRange(
  project: string,
  sel: WindowSel,
): Promise<CustomerUsageRangeEnvelope> {
  return request<CustomerUsageRangeEnvelope>(
    `/customers/${encodeURIComponent(project)}/usage/range?${windowQuery(sel)}`,
  )
}

/** No fixtures here by design (M12 R1): live security-governance state, never presented as sample data. */
export async function fetchRotations(): Promise<RotationListEnvelope> {
  return request<RotationListEnvelope>('/security/rotations')
}

export async function fetchVulns(): Promise<VulnListEnvelope> {
  return request<VulnListEnvelope>('/security/vulns')
}

export async function fetchAccessReviewCampaigns(): Promise<AccessReviewCampaignListEnvelope> {
  return request<AccessReviewCampaignListEnvelope>('/security/access-reviews')
}

export async function fetchAccessReviewCampaign(id: string): Promise<AccessReviewCampaignDetailEnvelope> {
  return request<AccessReviewCampaignDetailEnvelope>(`/security/access-reviews/${encodeURIComponent(id)}`)
}

export async function fetchBreakGlass(): Promise<BreakGlassListEnvelope> {
  return request<BreakGlassListEnvelope>('/security/break-glass')
}

export async function fetchTerminalReviews(): Promise<TerminalReviewListEnvelope> {
  return request<TerminalReviewListEnvelope>('/security/terminal-reviews')
}

/**
 * Pricing module (P2). No fixtures here by design — real, fleet-wide
 * pricing/billing state, never presented as sample data (same posture as
 * terminals/security/customers). config/pricebook/credits.grants come back
 * an honest zero/empty gap (source.kind:"none") when MDI_ADMIN_URL/
 * MDI_ADMIN_TOKEN are unset or mcd hasn't deployed the pricing routes yet.
 */
export async function fetchPricingConfig(): Promise<PricingConfigEnvelope> {
  return request<PricingConfigEnvelope>('/pricing/config')
}

export async function fetchPricebook(): Promise<PricebookListEnvelope> {
  return request<PricebookListEnvelope>('/pricing/pricebook')
}

/** The rack book — the book settlement ACTUALLY rates usage against. See RackBookEntry's doc comment. */
export async function fetchRackBook(): Promise<RackBookListEnvelope> {
  return request<RackBookListEnvelope>('/pricing/rackbook')
}

export async function fetchAccountCredits(): Promise<AccountCreditListEnvelope> {
  return request<AccountCreditListEnvelope>('/pricing/credits')
}

/**
 * Infrastructure Fleet (M13 S1/S2). No fixtures here by design — real,
 * live fleet cell/node state, never presented as sample data (same posture
 * as terminals/security/pricing).
 */
export async function fetchInfrastructureCells(): Promise<InfrastructureCellListEnvelope> {
  return request<InfrastructureCellListEnvelope>('/infrastructure/cells')
}

export async function fetchInfrastructureCell(id: string): Promise<InfrastructureCellDetailEnvelope> {
  return request<InfrastructureCellDetailEnvelope>(`/infrastructure/cells/${encodeURIComponent(id)}`)
}

export async function fetchInfrastructureNode(id: string): Promise<InfrastructureNodeDetailEnvelope> {
  return request<InfrastructureNodeDetailEnvelope>(`/infrastructure/nodes/${encodeURIComponent(id)}`)
}

/**
 * M14 Autopilot Command (docs/prd/03 D8). No fixtures here by design — real,
 * live autonomy/scope state, never presented as sample data (same posture as
 * infrastructure/security/pricing). Scope-mode toggles and the freeze button
 * mutate through invokeAction, never these read paths.
 */
export async function fetchAutopilotOverview(): Promise<AutopilotOverview> {
  return request<AutopilotOverview>('/autopilot/overview')
}

export async function fetchAutopilotInbox(): Promise<AutopilotExecutionsEnvelope> {
  return request<AutopilotExecutionsEnvelope>('/autopilot/inbox')
}

export async function fetchAutopilotFeed(): Promise<AutopilotExecutionsEnvelope> {
  return request<AutopilotExecutionsEnvelope>('/autopilot/feed')
}

/**
 * invokeAction drives the action gateway (FR-PLT-05) — the only mutation
 * path for alert.ack/silence/escalate/resolve etc. Unlike request(), a
 * non-2xx response here is still a valid, informative Execution body (a
 * gateway refusal is 403 with status:"refused"+refusalReason, a pending
 * step-up is 202) — so this never throws on the HTTP status, only on a
 * genuine network/auth failure.
 */
export async function invokeAction(actionId: string, payload: unknown, idempotencyKey: string): Promise<Execution> {
  const res = await fetch(`/admin/v2/actions/${encodeURIComponent(actionId)}:invoke`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(payload),
  })
  if (res.status === 401) {
    throw new AdminApiError(401, `invoke ${actionId} failed: unauthorized`)
  }
  return (await res.json()) as Execution
}

/** Adapt a wire Source block into a data-truth Provenance (same shape used by the widgets). */
function sourceProvenance(source: Source, note?: string) {
  return { source: source.kind, observedAt: source.observed_at, stale: source.stale, note }
}

function pct(fraction: number): number {
  return Math.round(fraction * 100)
}

/**
 * Compose the CEO Home dashboard from the REAL leadership snapshot. Every
 * KPI/security/incident number here traces to a live read model
 * (leadership-composed); the still-unbacked journeys + tenant-movements
 * widgets get empty arrays plus an honest "not wired yet" provenance note —
 * never fabricated rows (data-truth rule #2). See internal/leadership.
 */
export function mapLeadershipToHome(sum: LeadershipSummary, envObservedAt: string): HomeDashboardData {
  const fh = sum.fleetHealth
  const sec = sum.security

  const teamOpenIncidents = sum.teamThroughput.rows.reduce((n, r) => n + r.openIncidents, 0)

  const kpis: Kpi[] = [
    { label: 'Services healthy', value: fh.healthy, unit: `/${fh.total}`, provenance: sourceProvenance(fh.source, `${fh.unknown} unknown — no health signal wired yet (ADR-0003)`) },
    { label: 'SLO not configured', value: sum.slo.notConfigured, unit: 'services', provenance: sourceProvenance(sum.slo.source) },
    { label: 'Active incidents', value: sum.incidents.total, unit: 'open', provenance: sourceProvenance(sum.incidents.source) },
    { label: 'Open page alerts', value: sum.alerts.openPage, unit: 'page', provenance: sourceProvenance(sum.alerts.source) },
    { label: 'Open alerts', value: sum.alerts.openTotal, unit: 'total', provenance: sourceProvenance(sum.alerts.source) },
    { label: 'Rotations overdue', value: sec.overdueRotations, unit: `/${sec.totalRotations}`, provenance: sourceProvenance(sec.source, sec.lineage) },
    { label: 'Observability coverage', value: pct(sum.observability.fleetCoveragePct), unit: '%', provenance: sourceProvenance(sum.observability.source) },
    { label: 'Team open incidents', value: teamOpenIncidents, unit: `${sum.teamThroughput.rows.length} teams`, provenance: sourceProvenance(sum.teamThroughput.source, sum.teamThroughput.lineage) },
  ]

  // Real, derived security categories — every pct carries its raw counts in
  // the label so the meter is never a bare number without provenance.
  const rotationsCurrent = sec.totalRotations === 0 ? 100 : pct((sec.totalRotations - sec.overdueRotations) / sec.totalRotations)
  const vulnSlaClear = sec.totalVulns === 0 ? 100 : pct((sec.totalVulns - sec.slaBreachedVulns) / sec.totalVulns)
  const security: SecurityCategory[] = [
    { name: `Rotations current (${sec.totalRotations - sec.overdueRotations}/${sec.totalRotations})`, pct: rotationsCurrent },
    { name: `Vulns within SLA (${sec.totalVulns - sec.slaBreachedVulns}/${sec.totalVulns})`, pct: vulnSlaClear },
    { name: `Observability coverage (${sum.observability.servicesRanked} svc)`, pct: pct(sum.observability.fleetCoveragePct) },
  ]

  return {
    kpis,
    journeys: [],
    incidents: { total: sum.incidents.total, bySeverity: sum.incidents.bySeverity },
    security,
    tenants: [],
    provenance: {
      journeys: { source: 'not instrumented', observedAt: envObservedAt, note: 'no synthetic-journeys endpoint in /admin/v2 yet (FR-CAT-02)' },
      incidents: sourceProvenance(sum.incidents.source),
      security: sourceProvenance(sec.source, sec.lineage),
      tenants: { source: 'not instrumented', observedAt: envObservedAt, note: 'no billing tenant-movements feed wired yet' },
    },
  }
}

/**
 * The CEO/leadership Home dashboard — composed from the REAL
 * GET /admin/v2/leadership/summary snapshot (leadership-composed), never
 * fixtures. FIXTURE_HOME was deleted (real-integration-1). journeys +
 * tenant-movements render an honest "not wired yet" gap; every other tile is
 * a live number.
 */
export async function fetchHomeDashboard(): Promise<HomeDashboardData> {
  const env = await fetchLeadershipSummary()
  return mapLeadershipToHome(env.summary, env.source.observed_at)
}

/**
 * Leadership Command Center (M11: FR-LEAD-01/02/03). No fixtures here by
 * design — real, composed data from internal/leadership, never presented as
 * sample data (same posture as terminals/security/customers).
 */
export async function fetchLeadershipSummary(): Promise<LeadershipSummaryEnvelope> {
  return request<LeadershipSummaryEnvelope>('/leadership/summary')
}

// ---------------------------------------------------------------------------
// Studio agent backends (AI module). NOT admind: these talk to the Medhyai
// Studio daemon over its OWN dev proxy (/studio-agent → :8790, vite.config.ts),
// so they bypass request()/`/admin/v2` entirely. There is deliberately NO
// USE_FIXTURES branch — the no-fake-data law means we must never present a
// fabricated agent-config; when the daemon is down (the usual state on box-c)
// the caller surfaces an honest "unreachable" state instead of sample data.
// ---------------------------------------------------------------------------

/** Base for the studio daemon's agent-config, dev-proxied and prefix-stripped. */
const STUDIO_AGENT_CONFIG_PATH = '/studio-agent/agent-config'

/**
 * Client-side dial timeout. On box-c the studio port doesn't
 * connection-refuse — the dial HANGS (WSL2 firewall drops the packets), so
 * without an abort the fetch never rejects and /ai sits on "Checking…"
 * forever instead of reaching the honest unreachable state.
 */
const STUDIO_AGENT_TIMEOUT_MS = 5_000

/**
 * fetch() with the studio dial timeout, both GET and PUT. Implemented with an
 * explicit AbortController + setTimeout (equivalent to
 * AbortSignal.timeout(5000) in the init) so the timer is fake-timer
 * controllable in tests; a fired timeout is mapped to the same honest error
 * path as a refused connection.
 */
async function studioFetch(init?: RequestInit): Promise<Response> {
  const ctl = new AbortController()
  const timer = setTimeout(
    () => ctl.abort(new Error(`Studio daemon did not respond within ${STUDIO_AGENT_TIMEOUT_MS / 1000}s (dial hang)`)),
    STUDIO_AGENT_TIMEOUT_MS,
  )
  try {
    return await fetch(STUDIO_AGENT_CONFIG_PATH, { ...init, signal: ctl.signal })
  } catch (e) {
    // Our timeout fired: surface its reason (the honest message above), not
    // the raw AbortError the fetch rejects with.
    if (ctl.signal.aborted) {
      throw ctl.signal.reason instanceof Error ? ctl.signal.reason : new Error(String(ctl.signal.reason))
    }
    throw e
  } finally {
    clearTimeout(timer)
  }
}

export async function fetchStudioAgentConfig(): Promise<StudioAgentConfig> {
  // A network reject (daemon down), a dial hang (timeout above) or a non-2xx
  // all mean "not reachable / not authoritative" — surface it, never fall
  // back to defaults.
  const res = await studioFetch()
  if (!res.ok) throw new Error(`Studio daemon replied ${res.status} ${res.statusText}`)
  return (await res.json()) as StudioAgentConfig
}

export async function saveStudioAgentConfig(cfg: StudioAgentConfig): Promise<void> {
  const res = await studioFetch({
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(cfg),
  })
  if (!res.ok) throw new Error(`Studio daemon replied ${res.status} ${res.statusText}`)
}

/**
 * Grant (or debit, with a negative amount) Studio credits to any Studio
 * account, by email. Authorized by the shared STUDIO_ADMIN_TOKEN, which the
 * daemon holds; the operator supplies it here per-session (kept in memory, not
 * source) and it is sent only to the studio daemon over the /studio-agent
 * proxy. Same honest-unreachable contract as the agent-config calls.
 */
export async function grantStudioCredits(
  adminToken: string,
  email: string,
  amount: number,
  reason?: string,
): Promise<{ credits: number; granted: number; email: string }> {
  const ctl = new AbortController()
  const timer = setTimeout(() => ctl.abort(new Error('Studio daemon did not respond (dial hang)')), STUDIO_AGENT_TIMEOUT_MS)
  try {
    const res = await fetch('/studio-agent/auth/credits/grant', {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-admin-token': adminToken },
      body: JSON.stringify({ email, amount, reason }),
      signal: ctl.signal,
    })
    const data = (await res.json().catch(() => ({}))) as { user?: { email: string; credits: number }; granted?: number; error?: string }
    if (!res.ok || !data.user) throw new Error(data.error || `Studio daemon replied ${res.status} ${res.statusText}`)
    return { credits: data.user.credits, granted: data.granted ?? amount, email: data.user.email }
  } catch (e) {
    if (ctl.signal.aborted) throw ctl.signal.reason instanceof Error ? ctl.signal.reason : new Error(String(ctl.signal.reason))
    throw e
  } finally {
    clearTimeout(timer)
  }
}
